import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link, Redirect, Route, Switch, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import Breadcrumb from '../../Common/Breadcrumb';
import AmbassadorsAdmin from '../Ambassadors/AmbassadorsAdmin';
import PartnerAccounts from '../../Partner/PartnerAccounts';
import ClientMixPanel from './ClientMixPanel';
import PartnersFilterBar from './PartnersFilterBar';
import { formatAgreementSigned, readListFilters, reasonLabel } from './partnersListFilters';
import { Actions as COMMERCIAL } from '../../../store/actions/commercialActions';
import { toggleAlertMessage } from '../../../store/action';

const WL_TABS = ['programme', 'portfolio', 'worklist', 'accounts'];
const CONSULTANT_TABS = ['programme', 'portfolio', 'worklist'];

const signedDateCell = (f) => {
  const signed = formatAgreementSigned(f.agreementAcceptedAt);
  if (signed) return signed;
  return (
    <Badge variant='secondary' className='text-muted-foreground font-normal'>
      N/A
    </Badge>
  );
};

const MissingAgreementWorklist = ({ rows, loading, onOpen, emptyLabel }) => {
  if (loading) {
    return (
      <div className='flex gap-2 text-muted-foreground py-8'>
        <Loader2 className='animate-spin' size={18} /> Loading worklist…
      </div>
    );
  }
  if (!(rows || []).length) {
    return <p className='text-sm text-muted-foreground py-6'>{emptyLabel}</p>;
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-base'>Worklist</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Firm</TableHead>
              <TableHead>Reasons</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.firmId} className='border-border'>
                <TableCell className='font-medium'>{r.name}</TableCell>
                <TableCell>
                  <div className='flex flex-wrap gap-1'>
                    {(r.reasons || ['missing_agreement']).map((reason) => (
                      <Badge key={reason} variant='destructive' className='font-normal'>
                        {reasonLabel(reason)}
                      </Badge>
                    ))}
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>Re-engage outside the platform.</p>
                </TableCell>
                <TableCell>{r.orgStatus || '—'}</TableCell>
                <TableCell>
                  <Button size='sm' variant='outline' onClick={() => onOpen?.(r)}>
                    Open →
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

/** Keep tab + agreement/status query params in sync for a channel path. */
const useChannelListFilters = (pathname) => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const listFilters = useMemo(() => readListFilters(searchParams), [searchParams]);

  const replaceSearch = useCallback(
    (mutate) => {
      const params = new URLSearchParams(location.search);
      mutate(params);
      const qs = params.toString();
      history.replace({ pathname, search: qs ? `?${qs}` : '' });
    },
    [history, location.search, pathname]
  );

  const setListFilters = useCallback(
    ({ agreement, status }) => {
      replaceSearch((params) => {
        if (!agreement || agreement === 'all') params.delete('agreement');
        else params.set('agreement', agreement);
        if (!status || status === 'active') params.delete('status');
        else params.set('status', status);
      });
    },
    [replaceSearch]
  );

  const setTab = useCallback(
    (value, defaultTab = 'programme') => {
      replaceSearch((params) => {
        if (value === defaultTab) params.delete('tab');
        else params.set('tab', value);
      });
    },
    [replaceSearch]
  );

  return { searchParams, listFilters, setListFilters, setTab };
};

const CHART = { paying: '#2D6A4F', atRisk: '#BC6C25', churned: '#9B2226', inactive: '#6C757D' };
const money = (v) =>
  v == null || v === ''
    ? '—'
    : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(Number(v));

const pct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);

const lineOptions = (categories, series, yTitle) => ({
  chart: { type: 'line', height: 220, backgroundColor: 'transparent' },
  title: { text: undefined },
  credits: { enabled: false },
  xAxis: { categories },
  yAxis: { title: { text: yTitle } },
  series,
  legend: { enabled: series.length > 1 },
});

const QUADRANT_DOT = '#1B4332';

const Quadrant = ({ data, onSelect }) => {
  const points = data?.points || [];
  const maxSize = Math.max(...points.map((p) => p.size || 1), 1);
  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-base'>Health quadrant</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='relative h-56 rounded border border-border bg-card'>
          <div className='absolute left-1/2 top-0 bottom-0 w-px bg-border' />
          <div className='absolute top-1/2 left-0 right-0 h-px bg-border' />
          <div className='absolute top-1 left-2 text-xs text-muted-foreground'>Leaky growth</div>
          <div className='absolute top-1 right-2 text-xs text-muted-foreground'>Champions</div>
          <div className='absolute bottom-6 left-2 text-xs text-muted-foreground'>At risk</div>
          <div className='absolute bottom-6 right-2 text-xs text-muted-foreground'>Stable plateau</div>
          {points.map((p) => {
            const size = Math.max(14, 10 + (18 * (p.size || 1)) / maxSize);
            const x = Math.min(95, Math.max(5, Number(p.x) || 50));
            const y = Math.min(95, Math.max(5, 50 + (Number(p.y) || 0) / 2));
            return (
              <button
                key={p.id}
                type='button'
                title={`${p.name} · retention ${p.x} · growth ${p.y}`}
                aria-label={p.name}
                className='absolute z-10 rounded-full hover:scale-110 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground'
                style={{
                  width: size,
                  height: size,
                  left: `${x}%`,
                  bottom: `${y}%`,
                  marginLeft: -size / 2,
                  marginBottom: -size / 2,
                  backgroundColor: QUADRANT_DOT,
                  boxShadow: '0 0 0 2px #fff, 0 1px 4px rgba(0,0,0,.35)',
                }}
                onClick={() => onSelect?.(p)}
              />
            );
          })}
          <div className='absolute bottom-1 left-1/2 -translate-x-1/2 text-xs text-muted-foreground'>Retention →</div>
        </div>
        <p className='mt-2 text-xs text-muted-foreground'>
          Hover for summary; click opens firm Overview. Firm list lives under Portfolio.
        </p>
      </CardContent>
    </Card>
  );
};

const TlcTable = ({ rows, onOpen }) => (
  <Card>
    <CardHeader>
      <CardTitle className='text-base'>TLC queue</CardTitle>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Channel</TableHead>
            <TableHead>Firm</TableHead>
            <TableHead>Flag</TableHead>
            <TableHead>Why</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(rows || []).length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className='text-muted-foreground'>
                No TLC flags right now
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => (
              <TableRow
                key={`${r.channel}-${r.firmId || r.firmName}`}
                className='cursor-pointer hover:bg-muted/50'
                onClick={() => onOpen?.(r)}>
                <TableCell>{r.channel || '—'}</TableCell>
                <TableCell>{r.firmName}</TableCell>
                <TableCell>
                  <Badge variant='outline'>{r.flag}</Badge>
                </TableCell>
                <TableCell className='text-muted-foreground'>{r.reason}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const PartnersOverview = ({ getPartnersHubOverview, toggleAlertMessage }) => {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getPartnersHubOverview();
        if (!cancelled) {
          if (!res?.ok) throw new Error(res?.error || 'Failed to load Partners overview');
          setData(res.data);
        }
      } catch (e) {
        toggleAlertMessage?.(e?.message || 'Failed to load Partners overview', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getPartnersHubOverview, toggleAlertMessage]);

  if (loading) {
    return (
      <div className='flex items-center gap-2 p-8 text-muted-foreground'>
        <Loader2 className='animate-spin' size={18} /> Loading Partners overview…
      </div>
    );
  }

  const counts = data?.counts || {};
  return (
    <div className='space-y-4 p-4'>
      <Breadcrumb items={[{ label: 'Partners', link: '/admin/partners' }]} />
      <h1 className='text-2xl font-semibold text-foreground'>Partners overview</h1>
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Card>
          <CardContent className='pt-4'>
            <div className='text-2xl font-semibold'>{counts.activePartnerFirms ?? '—'}</div>
            <div className='text-sm text-muted-foreground'>Active partner firms</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='pt-4'>
            <div className='text-2xl font-semibold'>{counts.ambassadors ?? '—'}</div>
            <div className='text-sm text-muted-foreground'>Ambassadors</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='pt-4'>
            <div className='text-2xl font-semibold'>{counts.whiteLabels ?? '—'}</div>
            <div className='text-sm text-muted-foreground'>White Labels</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='pt-4'>
            <div className='text-2xl font-semibold'>{counts.consultants ?? '—'}</div>
            <div className='text-sm text-muted-foreground'>Consultants</div>
          </CardContent>
        </Card>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
        <Card>
          <CardContent className='pt-4'>
            <div className='text-2xl font-semibold'>{money(data?.revenueSnapshot?.whiteLabelMrr)}</div>
            <div className='text-sm text-muted-foreground'>White Label CYF MRR (now)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='pt-4'>
            <div className='text-2xl font-semibold'>{money(data?.revenueSnapshot?.consultantArr)}</div>
            <div className='text-sm text-muted-foreground'>Consultant CYF ARR (now)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='pt-4'>
            <div className='text-2xl font-semibold'>{money(data?.revenueSnapshot?.ambassadorAttributed12m)}</div>
            <div className='text-sm text-muted-foreground'>Ambassador attributed 12m</div>
          </CardContent>
        </Card>
      </div>
      {data?.revenueSnapshot?.note ? (
        <p className='text-xs text-muted-foreground'>{data.revenueSnapshot.note}</p>
      ) : null}
      <TlcTable
        rows={data?.tlcQueue}
        onOpen={(r) => {
          if (r.channel === 'Ambassador') history.push(`/admin/partners/ambassadors/${r.firmId}`);
          else if (r.channel === 'White Label') history.push(`/admin/partners/white-labels/${r.firmId}`);
          else if (r.channel === 'Consultant') history.push(`/admin/partners/consultants/${r.firmId}`);
        }}
      />
    </div>
  );
};

const AmbassadorsProgramme = ({ getPartnersAmbassadorsProgramme, toggleAlertMessage }) => {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let c = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getPartnersAmbassadorsProgramme();
        if (!c) {
          if (!res?.ok) throw new Error(res?.error || 'Failed to load Ambassadors programme');
          setData(res.data);
        }
      } catch (e) {
        toggleAlertMessage?.(e?.message || 'Failed to load Ambassadors programme', 'error');
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [getPartnersAmbassadorsProgramme, toggleAlertMessage]);

  if (loading) return <div className='p-8 text-muted-foreground flex gap-2 items-center'><Loader2 className='animate-spin' size={18} /> Loading…</div>;

  return (
    <div className='space-y-4 p-4'>
      <Breadcrumb items={[{ label: 'Partners', link: '/admin/partners' }, { label: 'Ambassadors', link: '/admin/partners/ambassadors' }]} />
      <h1 className='text-2xl font-semibold'>Ambassadors</h1>
      <div className='grid md:grid-cols-2 gap-4'>
        <TlcTable
          rows={(data?.tlcQueue || []).map((r) => ({ ...r, channel: 'Ambassador' }))}
          onOpen={(r) => history.push(`/admin/partners/ambassadors/${r.firmId}`)}
        />
        <Quadrant data={data?.quadrant} onSelect={(p) => history.push(`/admin/partners/ambassadors/${p.id}`)} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>Portfolio</CardTitle>
        </CardHeader>
        <CardContent>
          <Link className='text-primary underline text-sm' to='/admin/partners/ambassadors/manage'>
            Open Ambassadors portfolio, worklist and payouts
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

const FirmPortfolioTable = ({ firms, columns, onOpen, emptyLabel }) => {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return firms || [];
    return (firms || []).filter(
      (f) => String(f.name || '').toLowerCase().includes(q) || String(f.id || '').toLowerCase().includes(q)
    );
  }, [firms, query]);

  return (
    <div className='space-y-3'>
      <input
        type='search'
        placeholder='Search firms…'
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className='flex h-9 w-full max-w-sm rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm'
      />
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>Portfolio</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => (
                  <TableHead key={c.key} className={c.align === 'right' ? 'text-right' : undefined}>
                    {c.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className='text-muted-foreground'>
                    {emptyLabel}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((f) => (
                  <TableRow key={f.id} className='cursor-pointer hover:bg-muted/50' onClick={() => onOpen?.(f)}>
                    {columns.map((c) => (
                      <TableCell key={c.key} className={c.align === 'right' ? 'text-right' : undefined}>
                        {c.render(f)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const WhiteLabelsChannel = ({
  getPartnersWhiteLabelsProgramme,
  getPartnersWhiteLabelsWorklist,
  toggleAlertMessage,
}) => {
  const history = useHistory();
  const { searchParams, listFilters, setListFilters, setTab } = useChannelListFilters('/admin/partners/white-labels');
  const tabFromUrl = searchParams.get('tab');
  const activeTab = WL_TABS.includes(tabFromUrl) ? tabFromUrl : 'programme';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [worklist, setWorklist] = useState([]);
  const [loadingWorklist, setLoadingWorklist] = useState(false);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getPartnersWhiteLabelsProgramme(listFilters);
        if (!c) {
          if (!res?.ok) throw new Error(res?.error || 'Failed to load White Labels');
          setData(res.data);
        }
      } catch (e) {
        toggleAlertMessage?.(e?.message || 'Failed to load White Labels', 'error');
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [getPartnersWhiteLabelsProgramme, listFilters, toggleAlertMessage]);

  useEffect(() => {
    if (activeTab !== 'worklist') return undefined;
    let c = false;
    (async () => {
      try {
        setLoadingWorklist(true);
        const res = await getPartnersWhiteLabelsWorklist({ status: listFilters.status });
        if (!c) {
          if (!res?.ok) throw new Error(res?.error || 'Failed to load worklist');
          setWorklist(res.data?.rows || []);
        }
      } catch (e) {
        toggleAlertMessage?.(e?.message || 'Failed to load worklist', 'error');
      } finally {
        if (!c) setLoadingWorklist(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [activeTab, getPartnersWhiteLabelsWorklist, listFilters.status, toggleAlertMessage]);

  const chart = useMemo(() => {
    const rows = data?.programmeMrr || [];
    return lineOptions(
      rows.map((r) => r.month),
      [
        { name: 'MRR', data: rows.map((r) => r.mrr) },
        { name: 'Churn %', data: rows.map((r) => r.churnPct) },
      ],
      'MRR / churn'
    );
  }, [data]);

  const portfolioCols = [
    { key: 'name', label: 'White Label', render: (f) => f.name },
    { key: 'signed', label: 'Agreement signed', render: signedDateCell },
    { key: 'mrr', label: 'CYF MRR', align: 'right', render: (f) => money(f.mrr) },
    { key: 'growth', label: 'Δ', align: 'right', render: (f) => pct(f.revenueGrowthPct) },
    { key: 'churn', label: 'Churn', align: 'right', render: (f) => (f.churnPct != null ? `${f.churnPct}%` : '—') },
    { key: 'clients', label: 'Clients', align: 'right', render: (f) => f.billable ?? f.clients ?? '—' },
    {
      key: 'tlc',
      label: 'TLC',
      render: (f) => (f.tlc ? <Badge variant='outline'>{f.tlc}</Badge> : '—'),
    },
  ];

  return (
    <div className='p-6 space-y-4'>
      <Breadcrumb
        items={[
          { label: 'Partners', link: '/admin/partners' },
          { label: 'White Labels', link: '/admin/partners/white-labels' },
        ]}
      />
      <div>
        <h1 className='text-xl font-semibold text-foreground'>White Labels</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Programme health, portfolio and account controls. Firms without a signed Partner Reseller Agreement appear
          unless filtered out; Worklist lists missing agreements. Packages stay owned by the White Label.
        </p>
      </div>

      {(activeTab === 'programme' || activeTab === 'portfolio') && (
        <PartnersFilterBar
          agreement={listFilters.agreement}
          status={listFilters.status}
          onChange={setListFilters}
        />
      )}

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value='programme'>Programme</TabsTrigger>
          <TabsTrigger value='portfolio'>Portfolio</TabsTrigger>
          <TabsTrigger value='worklist'>Worklist</TabsTrigger>
          <TabsTrigger value='accounts'>Accounts</TabsTrigger>
        </TabsList>

        <TabsContent value='programme'>
          {loading ? (
            <div className='flex gap-2 text-muted-foreground py-8'>
              <Loader2 className='animate-spin' size={18} /> Loading…
            </div>
          ) : (
            <div className='space-y-4'>
              <div className='grid md:grid-cols-2 gap-4'>
                <Card>
                  <CardHeader>
                    <CardTitle className='text-base'>CYF MRR & churn (programme)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <HighchartsReact highcharts={Highcharts} options={chart} />
                    {data?.metricsNote ? <p className='mt-2 text-xs text-muted-foreground'>{data.metricsNote}</p> : null}
                  </CardContent>
                </Card>
                <TlcTable
                  rows={(data?.tlcQueue || []).map((r) => ({ ...r, channel: 'White Label' }))}
                  onOpen={(r) => history.push(`/admin/partners/white-labels/${r.firmId}`)}
                />
              </div>
              <Quadrant data={data?.quadrant} onSelect={(p) => history.push(`/admin/partners/white-labels/${p.id}`)} />
              <ClientMixPanel mix={data?.clientMix} title='Client mix (programme)' />
            </div>
          )}
        </TabsContent>

        <TabsContent value='portfolio'>
          {loading ? (
            <div className='flex gap-2 text-muted-foreground py-8'>
              <Loader2 className='animate-spin' size={18} /> Loading…
            </div>
          ) : (
            <FirmPortfolioTable
              firms={data?.firms || []}
              columns={portfolioCols}
              emptyLabel='No White Labels match the current Agreement and firm-status filters.'
              onOpen={(f) => history.push(`/admin/partners/white-labels/${f.id}`)}
            />
          )}
        </TabsContent>

        <TabsContent value='worklist'>
          <MissingAgreementWorklist
            rows={worklist}
            loading={loadingWorklist}
            emptyLabel='No White Labels are missing a signed Agreement.'
            onOpen={(r) => history.push(`/admin/partners/white-labels/${r.firmId}`)}
          />
        </TabsContent>

        <TabsContent value='accounts'>
          <div className='space-y-3'>
            <p className='text-sm text-muted-foreground'>
              Client account Plan, FTE and Demo controls for a White Label (formerly Partner accounts).
            </p>
            <PartnerAccounts embedded />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const WhiteLabelFirm = ({ getPartnersWhiteLabelFirm, setDemoFlag, toggleAlertMessage }) => {
  const history = useHistory();
  const { whiteLabelOrgId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getPartnersWhiteLabelFirm(whiteLabelOrgId);
      if (!res?.ok) throw new Error(res?.error || 'Failed to load White Label firm');
      setData(res.data);
    } catch (e) {
      toggleAlertMessage?.(e?.message || 'Failed to load White Label firm', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [whiteLabelOrgId]);

  const accounts = data?.accounts || [];
  const mix = useMemo(() => {
    const demo = accounts.filter((a) => a.demo).length;
    const active = accounts.filter((a) => !a.demo && String(a.status || '').toLowerCase() === 'active').length;
    const inactive = Math.max(0, accounts.length - demo - active);
    return { active, inactive, demo, total: accounts.length };
  }, [accounts]);

  const revenueChart = useMemo(() => {
    const mrrSeries = data?.firm?.mrrSeries || [];
    return {
      chart: { type: 'line', height: 240, backgroundColor: 'transparent' },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: { categories: mrrSeries.map((m) => m.month) },
      yAxis: { title: { text: 'CYF MRR' } },
      series: [{ name: 'CYF MRR', data: mrrSeries.map((m) => m.mrr), color: CHART.paying }],
      legend: { enabled: false },
    };
  }, [data]);

  const mixChart = useMemo(
    () => ({
      chart: { type: 'pie', height: 240, backgroundColor: 'transparent' },
      title: { text: undefined },
      credits: { enabled: false },
      plotOptions: { pie: { dataLabels: { enabled: true, format: '{point.name}: {point.y}' } } },
      series: [
        {
          name: 'Clients',
          data: [
            { name: 'Active', y: mix.active, color: CHART.paying },
            { name: 'Inactive', y: mix.inactive, color: CHART.inactive },
            { name: 'Demo', y: mix.demo, color: CHART.atRisk },
          ].filter((d) => d.y > 0),
        },
      ],
    }),
    [mix]
  );

  if (loading) {
    return (
      <div className='p-8 flex gap-2 text-muted-foreground'>
        <Loader2 className='animate-spin' size={18} /> Loading…
      </div>
    );
  }
  const firm = data?.firm || {};
  const latestMove = firm.movement?.slice(-1)[0];

  return (
    <div className='space-y-6 p-4 md:p-6'>
      <Breadcrumb
        items={[
          { label: 'Partners', link: '/admin/partners' },
          { label: 'White Labels', link: '/admin/partners/white-labels' },
          { label: firm.name || 'Overview' },
        ]}
      />
      <div className='flex flex-wrap items-center gap-3'>
        <Button variant='ghost' size='sm' onClick={() => history.push('/admin/partners/white-labels?tab=portfolio')}>
          <ArrowLeft size={16} strokeWidth={1.5} className='mr-2' />
          Back to Portfolio
        </Button>
        {firm.tlc ? <Badge variant='outline'>{firm.tlc}</Badge> : null}
        {firm.domain ? <Badge variant='secondary'>{firm.domain}</Badge> : null}
      </div>
      <div>
        <h1 className='text-2xl font-semibold text-foreground'>{firm.name}</h1>
        <p className='text-sm text-muted-foreground'>
          Partner Manager: Demo toggle only · Packages owned by the White Label
        </p>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Clients</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{mix.total}</CardContent>
        </Card>
        <Card className='border-border/80 border-l-4' style={{ borderLeftColor: CHART.paying }}>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Billable</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.paying }}>
            {firm.billable ?? '—'}
          </CardContent>
        </Card>
        <Card className='border-border/80 border-l-4' style={{ borderLeftColor: CHART.atRisk }}>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Demo</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.atRisk }}>
            {mix.demo}
          </CardContent>
        </Card>
        <Card className='border-border/80 border-l-4' style={{ borderLeftColor: CHART.churned }}>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Inactive</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.churned }}>
            {mix.inactive}
          </CardContent>
        </Card>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>CYF MRR</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{money(firm.mrr)}</CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>MRR growth</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{pct(firm.revenueGrowthPct)}</CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Added (latest)</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{latestMove?.added ?? '—'}</CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Lost (latest)</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{latestMove?.lost ?? '—'}</CardContent>
        </Card>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <Card className='border-border/80'>
          <CardHeader className='pb-1'>
            <CardTitle>CYF revenue trend</CardTitle>
            <p className='text-xs text-muted-foreground font-normal'>Approximate until fee history is wired</p>
          </CardHeader>
          <CardContent>
            <HighchartsReact highcharts={Highcharts} options={revenueChart} />
          </CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-1'>
            <CardTitle>Status mix</CardTitle>
            <p className='text-xs text-muted-foreground font-normal'>Active · Inactive · Demo</p>
          </CardHeader>
          <CardContent>
            {mix.total ? (
              <HighchartsReact highcharts={Highcharts} options={mixChart} />
            ) : (
              <p className='text-sm text-muted-foreground py-8'>No client accounts yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <ClientMixPanel mix={data?.clientMix || firm.clientMix} title='Plan, FTE & add-ons' />

      <Card>
        <CardHeader>
          <CardTitle>Client accounts</CardTitle>
        </CardHeader>
        <CardContent className='overflow-x-auto'>
          <Table>
            <TableHeader>
              <TableRow className='border-border'>
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>FTE</TableHead>
                <TableHead className='text-right'>CYF fee / mo</TableHead>
                <TableHead>Demo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className='text-muted-foreground'>
                    No accounts
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((a) => (
                  <TableRow key={a.id} className='border-border'>
                    <TableCell className='font-medium'>{a.name}</TableCell>
                    <TableCell>
                      <Badge variant={String(a.status || '').toLowerCase() === 'active' ? 'default' : 'secondary'}>
                        {a.status || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>{a.packageName || '—'}</TableCell>
                    <TableCell>{a.fteBand || '—'}</TableCell>
                    <TableCell className='text-right'>{money(a.cyfFeeMonthly)}</TableCell>
                    <TableCell>
                      <Button
                        size='sm'
                        variant={a.demo ? 'default' : 'outline'}
                        onClick={async () => {
                          try {
                            await setDemoFlag(a.id, !a.demo);
                            toggleAlertMessage?.('Demo flag updated', 'success');
                            load();
                          } catch (e) {
                            toggleAlertMessage?.(e?.message || 'Demo update failed', 'error');
                          }
                        }}>
                        {a.demo ? 'On' : 'Off'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const ConsultantsChannel = ({
  getPartnersConsultantsProgramme,
  getPartnersConsultantsWorklist,
  toggleAlertMessage,
}) => {
  const history = useHistory();
  const { searchParams, listFilters, setListFilters, setTab } = useChannelListFilters('/admin/partners/consultants');
  const tabFromUrl = searchParams.get('tab');
  const activeTab = CONSULTANT_TABS.includes(tabFromUrl) ? tabFromUrl : 'programme';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [worklist, setWorklist] = useState([]);
  const [loadingWorklist, setLoadingWorklist] = useState(false);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getPartnersConsultantsProgramme(listFilters);
        if (!c) {
          if (!res?.ok) throw new Error(res?.error || 'Failed to load Consultants');
          setData(res.data);
        }
      } catch (e) {
        toggleAlertMessage?.(e?.message || 'Failed to load Consultants', 'error');
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [getPartnersConsultantsProgramme, listFilters, toggleAlertMessage]);

  useEffect(() => {
    if (activeTab !== 'worklist') return undefined;
    let c = false;
    (async () => {
      try {
        setLoadingWorklist(true);
        const res = await getPartnersConsultantsWorklist({ status: listFilters.status });
        if (!c) {
          if (!res?.ok) throw new Error(res?.error || 'Failed to load worklist');
          setWorklist(res.data?.rows || []);
        }
      } catch (e) {
        toggleAlertMessage?.(e?.message || 'Failed to load worklist', 'error');
      } finally {
        if (!c) setLoadingWorklist(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [activeTab, getPartnersConsultantsWorklist, listFilters.status, toggleAlertMessage]);

  const chart = useMemo(() => {
    const rows = data?.programmeArr || [];
    return lineOptions(
      rows.map((r) => r.month),
      [
        { name: 'ARR', data: rows.map((r) => r.arr) },
        { name: 'Churn %', data: rows.map((r) => r.churnPct) },
      ],
      'ARR / churn'
    );
  }, [data]);

  const portfolioCols = [
    { key: 'name', label: 'Consultant', render: (f) => f.name },
    { key: 'signed', label: 'Agreement signed', render: signedDateCell },
    { key: 'arr', label: 'CYF ARR', align: 'right', render: (f) => money(f.arr) },
    { key: 'growth', label: 'Δ', align: 'right', render: (f) => pct(f.revenueGrowthPct) },
    { key: 'churn', label: 'Churn', align: 'right', render: (f) => (f.churnPct != null ? `${f.churnPct}%` : '—') },
    { key: 'usable', label: 'Usable', align: 'right', render: (f) => f.usable ?? '—' },
    {
      key: 'tlc',
      label: 'TLC',
      render: (f) => (f.tlc ? <Badge variant='outline'>{f.tlc}</Badge> : '—'),
    },
  ];

  return (
    <div className='p-6 space-y-4'>
      <Breadcrumb
        items={[
          { label: 'Partners', link: '/admin/partners' },
          { label: 'Consultants', link: '/admin/partners/consultants' },
        ]}
      />
      <div>
        <h1 className='text-xl font-semibold text-foreground'>Consultants</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Programme health and firm portfolio. Firms without a signed Agreement appear unless filtered out; Worklist
          lists missing agreements. Commercial edits stay on Consultant Admin.
        </p>
      </div>

      {(activeTab === 'programme' || activeTab === 'portfolio') && (
        <PartnersFilterBar
          agreement={listFilters.agreement}
          status={listFilters.status}
          onChange={setListFilters}
        />
      )}

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value='programme'>Programme</TabsTrigger>
          <TabsTrigger value='portfolio'>Portfolio</TabsTrigger>
          <TabsTrigger value='worklist'>Worklist</TabsTrigger>
        </TabsList>

        <TabsContent value='programme'>
          {loading ? (
            <div className='flex gap-2 text-muted-foreground py-8'>
              <Loader2 className='animate-spin' size={18} /> Loading…
            </div>
          ) : (
            <div className='space-y-4'>
              <div className='grid md:grid-cols-2 gap-4'>
                <Card>
                  <CardHeader>
                    <CardTitle className='text-base'>CYF ARR & churn (programme)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <HighchartsReact highcharts={Highcharts} options={chart} />
                    {data?.metricsNote ? <p className='mt-2 text-xs text-muted-foreground'>{data.metricsNote}</p> : null}
                  </CardContent>
                </Card>
                <TlcTable
                  rows={(data?.tlcQueue || []).map((r) => ({ ...r, channel: 'Consultant' }))}
                  onOpen={(r) => history.push(`/admin/partners/consultants/${r.firmId}`)}
                />
              </div>
              <Quadrant data={data?.quadrant} onSelect={(p) => history.push(`/admin/partners/consultants/${p.id}`)} />
              <ClientMixPanel mix={data?.clientMix} title='Client mix (programme)' />
            </div>
          )}
        </TabsContent>

        <TabsContent value='portfolio'>
          {loading ? (
            <div className='flex gap-2 text-muted-foreground py-8'>
              <Loader2 className='animate-spin' size={18} /> Loading…
            </div>
          ) : (
            <FirmPortfolioTable
              firms={data?.firms || []}
              columns={portfolioCols}
              emptyLabel='No Consultants match the current Agreement and firm-status filters.'
              onOpen={(f) => history.push(`/admin/partners/consultants/${f.id}`)}
            />
          )}
        </TabsContent>

        <TabsContent value='worklist'>
          <MissingAgreementWorklist
            rows={worklist}
            loading={loadingWorklist}
            emptyLabel='No Consultants are missing a signed Agreement.'
            onOpen={(r) => history.push(`/admin/partners/consultants/${r.firmId}`)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ConsultantFirm = ({ getPartnersConsultantFirm, toggleAlertMessage }) => {
  const history = useHistory();
  const { consultantOrgId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let c = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getPartnersConsultantFirm(consultantOrgId);
        if (!c) {
          if (!res?.ok) throw new Error(res?.error || 'Failed to load Consultant firm');
          setData(res.data);
        }
      } catch (e) {
        toggleAlertMessage?.(e?.message || 'Failed to load Consultant firm', 'error');
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [consultantOrgId, getPartnersConsultantFirm, toggleAlertMessage]);

  const accounts = data?.accounts || [];
  const mix = useMemo(() => {
    const active = accounts.filter((a) => String(a.status || '').toLowerCase() === 'active').length;
    const attention = (data?.enablementQueue || []).length;
    const inactive = Math.max(0, accounts.length - active);
    return { active, inactive, attention, total: accounts.length };
  }, [accounts, data]);

  const revenueChart = useMemo(() => {
    const arrSeries = data?.firm?.arrSeries || [];
    return {
      chart: { type: 'line', height: 240, backgroundColor: 'transparent' },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: { categories: arrSeries.map((m) => m.month) },
      yAxis: { title: { text: 'CYF ARR' } },
      series: [{ name: 'CYF ARR', data: arrSeries.map((m) => m.arr), color: CHART.paying }],
      legend: { enabled: false },
    };
  }, [data]);

  const mixChart = useMemo(
    () => ({
      chart: { type: 'pie', height: 240, backgroundColor: 'transparent' },
      title: { text: undefined },
      credits: { enabled: false },
      plotOptions: { pie: { dataLabels: { enabled: true, format: '{point.name}: {point.y}' } } },
      series: [
        {
          name: 'Clients',
          data: [
            { name: 'Active', y: mix.active, color: CHART.paying },
            { name: 'Inactive', y: mix.inactive, color: CHART.inactive },
          ].filter((d) => d.y > 0),
        },
      ],
    }),
    [mix]
  );

  if (loading) {
    return (
      <div className='p-8 flex gap-2 text-muted-foreground'>
        <Loader2 className='animate-spin' size={18} /> Loading…
      </div>
    );
  }
  const firm = data?.firm || {};

  return (
    <div className='space-y-6 p-4 md:p-6'>
      <Breadcrumb
        items={[
          { label: 'Partners', link: '/admin/partners' },
          { label: 'Consultants', link: '/admin/partners/consultants' },
          { label: firm.name || 'Overview' },
        ]}
      />
      <div className='flex flex-wrap items-center gap-3'>
        <Button variant='ghost' size='sm' onClick={() => history.push('/admin/partners/consultants?tab=portfolio')}>
          <ArrowLeft size={16} strokeWidth={1.5} className='mr-2' />
          Back to Portfolio
        </Button>
        {firm.tlc ? <Badge variant='outline'>{firm.tlc}</Badge> : null}
        <Badge variant='secondary'>Read-only</Badge>
      </div>
      <div>
        <h1 className='text-2xl font-semibold text-foreground'>{firm.name}</h1>
        <p className='text-sm text-muted-foreground'>
          Enablement roll-up · Commercial edits stay on Consultant Admin
        </p>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Clients</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{mix.total || firm.clients || '—'}</CardContent>
        </Card>
        <Card className='border-border/80 border-l-4' style={{ borderLeftColor: CHART.paying }}>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Usable / active</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.paying }}>
            {firm.usable ?? mix.active}
          </CardContent>
        </Card>
        <Card className='border-border/80 border-l-4' style={{ borderLeftColor: CHART.atRisk }}>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Attention</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.atRisk }}>
            {mix.attention}
          </CardContent>
        </Card>
        <Card className='border-border/80 border-l-4' style={{ borderLeftColor: CHART.churned }}>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Churn</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums' style={{ color: CHART.churned }}>
            {firm.churnPct != null ? `${firm.churnPct}%` : '—'}
          </CardContent>
        </Card>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-3 gap-3'>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>CYF ARR</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{money(firm.arr)}</CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>ARR growth</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>{pct(firm.revenueGrowthPct)}</CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>Retention</CardTitle>
          </CardHeader>
          <CardContent className='text-2xl font-semibold tabular-nums'>
            {firm.retentionPct != null ? `${firm.retentionPct}%` : '—'}
          </CardContent>
        </Card>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <Card className='border-border/80'>
          <CardHeader className='pb-1'>
            <CardTitle>CYF revenue trend</CardTitle>
            <p className='text-xs text-muted-foreground font-normal'>Approximate until fee history is wired</p>
          </CardHeader>
          <CardContent>
            <HighchartsReact highcharts={Highcharts} options={revenueChart} />
          </CardContent>
        </Card>
        <Card className='border-border/80'>
          <CardHeader className='pb-1'>
            <CardTitle>Status mix</CardTitle>
          </CardHeader>
          <CardContent>
            {mix.total ? (
              <HighchartsReact highcharts={Highcharts} options={mixChart} />
            ) : (
              <p className='text-sm text-muted-foreground py-8'>No client accounts yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <ClientMixPanel mix={data?.clientMix || firm.clientMix} title='Plan, FTE & add-ons' />

      <div className='grid md:grid-cols-2 gap-4'>
        <Card>
          <CardHeader>
            <CardTitle>Enablement queue</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Signal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.enablementQueue || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className='text-muted-foreground'>
                      No signals
                    </TableCell>
                  </TableRow>
                ) : (
                  data.enablementQueue.map((e) => (
                    <TableRow key={e.clientId}>
                      <TableCell>{e.clientName}</TableCell>
                      <TableCell>
                        <Badge variant='outline'>{e.signal}</Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Client accounts</CardTitle>
          </CardHeader>
          <CardContent className='overflow-x-auto'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className='text-right'>ARR signal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className='text-muted-foreground'>
                      No accounts
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className='font-medium'>{a.name}</TableCell>
                      <TableCell>
                        <Badge variant={String(a.status || '').toLowerCase() === 'active' ? 'default' : 'secondary'}>
                          {a.status || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-right'>{money(a.arr)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const PartnersHub = (props) => {
  const { path } = useRouteMatch();
  return (
    <Switch>
      <Route exact path={path} render={() => <PartnersOverview {...props} />} />
      <Route exact path={`${path}/ambassadors`} component={AmbassadorsAdmin} />
      <Route
        path={`${path}/ambassadors/:ambassadorOrgId`}
        render={({ match }) => <Redirect to={`/admin/ambassadors/${match.params.ambassadorOrgId}`} />}
      />
      <Route
        exact
        path={`${path}/white-labels/accounts`}
        render={() => <Redirect to='/admin/partners/white-labels?tab=accounts' />}
      />
      <Route exact path={`${path}/white-labels`} render={() => <WhiteLabelsChannel {...props} />} />
      <Route exact path={`${path}/white-labels/:whiteLabelOrgId`} render={() => <WhiteLabelFirm {...props} />} />
      <Route exact path={`${path}/consultants`} render={() => <ConsultantsChannel {...props} />} />
      <Route exact path={`${path}/consultants/:consultantOrgId`} render={() => <ConsultantFirm {...props} />} />
    </Switch>
  );
};

const mapDispatch = {
  ...COMMERCIAL,
  toggleAlertMessage,
};

export default connect(null, mapDispatch)(PartnersHub);

/** Legacy Ambassadors Admin path → Partners (R-8050). */
export const AmbassadorsAdminRedirect = () => <Redirect to='/admin/partners/ambassadors' />;
